import { Skeleton, useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSCard from '../../../design-system/Card/DSCard';
import { DSTypography } from '../../../design-system/Typography';

export type CollectionStatsProps = {
  disabled?: boolean;
  totalPurchases?: number;
  totalSales?: number;
  // A sponsored collection is given away, not sold: show its redemptions
  // instead of purchases and revenue.
  sponsored?: boolean;
  totalRedemptions?: number;
  hasError?: boolean;
  isLoading?: boolean;
};

const CollectionStats = ({
  disabled = false,
  totalPurchases = 0,
  totalSales = 0,
  sponsored = false,
  totalRedemptions = 0,
  hasError = false,
  isLoading = false,
}: CollectionStatsProps) => {
  const { spacing, palette } = useTheme();

  const isEmptyData = sponsored
    ? totalRedemptions === 0
    : totalPurchases === 0 && totalSales === 0;
  const statLabels = sponsored
    ? ['Downloads']
    : ['Total Purchases', 'Total Revenue*'];

  const cardStyle = {
    padding: spacing(1, 2.25),
    backgroundColor: palette.paper.elevation5,
    borderRadius: spacing(1.33),
    opacity: disabled ? 0.3 : 1,
    minWidth: spacing(17),
  };

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column', sm: 'row' },
          gap: spacing(2),
        }}
      >
        {statLabels.map((label) => (
          <DSCard key={label} sx={cardStyle}>
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: spacing(0.5),
                textAlign: 'center',
              }}
            >
              <Skeleton variant="text" width={spacing(6)} height={spacing(4)} />
              <DSTypography variant="body2">{label}</DSTypography>
            </DSBox>
          </DSCard>
        ))}
      </DSBox>
    );
  }

  if (hasError) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(2),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: spacing(2),
          }}
        >
          <DSCard sx={cardStyle}>
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(0.5),
                textAlign: 'center',
                padding: spacing(1),
              }}
            >
              <DSTypography variant="body2" sx={{ color: palette.error.main }}>
                Unable to load statistics
              </DSTypography>
            </DSBox>
          </DSCard>
        </DSBox>
      </DSBox>
    );
  }

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(2),
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column', sm: 'row' },
          gap: spacing(2),
        }}
      >
        {sponsored && (
          <DSCard sx={cardStyle}>
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(0.5),
                textAlign: 'center',
              }}
            >
              <DSTypography variant="h5">{totalRedemptions}</DSTypography>
              <DSTypography variant="body2">Downloads</DSTypography>
            </DSBox>
          </DSCard>
        )}

        {!sponsored && (
          <>
            <DSCard sx={cardStyle}>
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing(0.5),
                  textAlign: 'center',
                }}
              >
                <DSTypography variant="h5">{totalPurchases}</DSTypography>
                <DSTypography variant="body2">Total Purchases</DSTypography>
              </DSBox>
            </DSCard>

            <DSCard sx={cardStyle}>
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing(0.5),
                  textAlign: 'center',
                }}
              >
                <DSTypography variant="h5"> ${totalSales}</DSTypography>
                <DSTypography variant="body2">Total Revenue*</DSTypography>
              </DSBox>
            </DSCard>
          </>
        )}
      </DSBox>

      {!sponsored && !disabled && !isEmptyData && (
        <DSTypography variant="body2">
          *The earnings shown here are estimates only. Your revenue payout will
          be calculated accurately at the time of payment.
        </DSTypography>
      )}

      {isEmptyData && !disabled && (
        <DSTypography variant="body2" sx={{ color: palette.text.secondary }}>
          {sponsored ? 'No downloads yet' : 'No sales data yet'}
        </DSTypography>
      )}
    </DSBox>
  );
};

export default CollectionStats;
